import { useEffect, useState } from "react";
import { dayjs } from "@boo/shared";
import type { NetWorthResponse } from "@boo/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import { fetchNetWorth } from "@/services/accountApi";
import { useAccountStore } from "../store/useAccountStore";
import { formatBaht } from "../../bills/lib/billMath";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const YEAR_OPTIONS = Array.from({ length: 4 }, (_, i) => dayjs().year() - i);

export default function NetWorthChart() {
  const [year, setYear] = useState(dayjs().year());
  const [data, setData] = useState<NetWorthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // New balances change the trend, so refetch when they do.
  const balances = useAccountStore((s) => s.balances);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const result = await fetchNetWorth(year);
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [year, balances]);

  const points = data?.points ?? [];
  const maxValue = Math.max(...points.map((p) => Math.abs(p.netWorth)), 1);
  const untracked = Math.max(...points.map((p) => p.untrackedAccounts), 0);

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-semibold">Net worth</CardTitle>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          aria-label="Year"
          className="rounded-lg border border-border bg-transparent px-2 py-1 text-xs text-foreground"
        >
          {YEAR_OPTIONS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </CardHeader>
      <CardContent>
        {loading ? (
          <SkeletonRegion className="space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-4" />
            ))}
          </SkeletonRegion>
        ) : points.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No month-end balances for {year} yet — set a balance on an account
            to start the trend.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {points.map((point) => (
              <div
                key={point.month}
                className="flex items-center gap-3 text-xs [font-variant-numeric:tabular-nums]"
              >
                <span className="w-8 text-muted-foreground">
                  {MONTH_NAMES[point.month - 1]}
                </span>
                <div className="flex-1 h-3 rounded-sm bg-[var(--surface-muted)]">
                  <div
                    className={`h-full rounded-sm ${
                      point.netWorth < 0 ? "bg-red-500/70" : "bg-primary/70"
                    }`}
                    style={{
                      width: `${(Math.abs(point.netWorth) / maxValue) * 100}%`,
                    }}
                  />
                </div>
                <span className="w-24 text-right text-foreground">
                  {formatBaht(point.netWorth)}
                </span>
              </div>
            ))}
            {untracked > 0 && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                {untracked} account{untracked === 1 ? "" : "s"} not yet tracked
                in earlier months — set a balance to include{" "}
                {untracked === 1 ? "it" : "them"}.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
